import { useLocation } from '@reach/router';
import React, { useEffect, useState } from 'react';
import { IconNavigationAddPlusNormal, IconNavigationAddSubtractMinusNormal } from './Icons';

import classnames from 'classnames';
import { LANGUAGE_REFERENCE_PATH } from '../utils/constants';
import './Toc.scss';

interface TocProps {
  toc: {
    items: Array<any>;
  };
}

interface TocItemProps {
  item: {
    title: string;
    url: string;
    items: Array<any>;
  };
  firstCategory?: boolean;
  nestedItem?: boolean;
  leafItem?: boolean;
}

const TocItem = ({ item, firstCategory, nestedItem, leafItem }: TocItemProps) => {
  const [openItem, setOpenItem] = useState(false);
  const [elementActive, setElementActive] = useState('');
  const [firstTime, setFirstTime] = useState(false);

  useEffect(() => {
    // Hightlight the first element of tutorial sidebar
    if (!firstTime && document.getElementsByTagName('h2')[0]) {
      setElementActive(document.getElementsByTagName('h2')[0]?.innerText);
      setFirstTime(true);
    }
    function onScroll() {
      //Manage the active elements in ToC
      let visibleElements = [];
      let visibleFocusElement = [];
      // loop for all H2, H3, H4 inside markdown, and check it as active is are on the screen.
      (document.getElementsByTagName('h2') as any as Array<HTMLElement>).forEach((el) => {
        if (el !== null) {
          let rect = el.getBoundingClientRect();
          if (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
            visibleElements.indexOf(el.innerText)
          ) {
            visibleElements.push(el.innerText);
            visibleFocusElement.push(el);
          }
        }
      });
      (document.getElementsByTagName('h3') as any as Array<HTMLElement>).forEach((el) => {
        if (el !== null) {
          let rect = el.getBoundingClientRect();
          if (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
            visibleElements.indexOf(el.innerText)
          ) {
            visibleElements.push(el.innerText);
            visibleFocusElement.push(el);
          }
        }
      });
      (document.getElementsByTagName('h4') as any as Array<HTMLElement>).forEach((el) => {
        if (el !== null) {
          let rect = el.getBoundingClientRect();
          if (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
            visibleElements.indexOf(el.innerText)
          ) {
            visibleElements.push(el.innerText);
            visibleFocusElement.push(el);
          }
        }
      });
      visibleElements = visibleElements.filter((el) => el !== 'Title');

      if (visibleElements[0]) {
        setElementActive(visibleElements[0]);
      }
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [firstTime]);

  const nestedItems = (item.items || []).map((item) => {
    return <TocItem key={item.url} item={item} nestedItem={true} leafItem={item.items?.length > 0} />;
  });

  useEffect(() => {
    if (elementActive === item.title) {
      setOpenItem(true);
      document.getElementsByClassName('focus')[0].scrollIntoView({ block: 'nearest' }); // Manage scroll of ToC
    }
  }, [elementActive, item.title]);

  return (
    <li>
      <div className="item">
        <a
          href={item.url}
          className={classnames({
            focus: elementActive === item.title,
            active: elementActive === item.title,
            'first-category': firstCategory,
            nestedItem: nestedItem,
            leafItem: leafItem,
          })}
        >
          <span>{item.title}</span>
          <span>
            {item.items && openItem ? (
              <div className="icon">
                <IconNavigationAddSubtractMinusNormal onClick={() => setOpenItem(false)} />
              </div>
            ) : item.items && !openItem ? (
              <div className="icon">
                <IconNavigationAddPlusNormal onClick={() => setOpenItem(true)} />
              </div>
            ) : null}
          </span>
        </a>
      </div>
      {item.items && openItem ? <ul className={classnames({ leafItem: leafItem })}>{nestedItems}</ul> : null}
    </li>
  );
};

const Toc = ({ toc }: TocProps) => {
  const location = useLocation();
  //Don't show Toc for Language Reference Page
  if (location.pathname.includes(LANGUAGE_REFERENCE_PATH) || !toc.items) return;
  return (
    <div className="toc">
      <h5 id="ToCtitle">ON THIS PAGE</h5>
      <div className="scroller">
        <ul>{toc.items?.map((item) => <TocItem firstCategory={true} key={item.url} item={item} />)}</ul>
      </div>
    </div>
  );
};

export default Toc;
